import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const ASSET_URL = (path) => (path?.startsWith("http") ? path : `${BACKEND_URL}${path}`);

const TOKEN_KEY = "drb_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const client = axios.create({ baseURL: API });
client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const api = {
  // PUBLIC
  pages: () => client.get("/public/pages").then(r => r.data),
  page: (slug) => client.get(`/public/pages/${slug}`).then(r => r.data),
  faq: () => client.get("/public/faq").then(r => r.data),
  glossary: () => client.get("/public/glossary").then(r => r.data),
  news: () => client.get("/public/news").then(r => r.data),
  newsBySlug: (s) => client.get(`/public/news/${s}`).then(r => r.data),
  press: () => client.get("/public/press").then(r => r.data),
  pricing: () => client.get("/public/pricing").then(r => r.data),
  pricingSettings: () => client.get("/public/pricing-settings").then(r => r.data),
  settings: () => client.get("/public/settings").then(r => r.data),
  seoJsonLd: (slug, lang = "de") => client.get(`/seo/jsonld/${slug}`, { params: { lang } }).then(r => r.data),
  media: (type) => client.get("/public/media", { params: type ? { media_type: type } : {} }).then(r => r.data),
  submitPraxisCheck: (data) => client.post("/public/submissions", data).then(r => r.data),
  formToken: (type) => client.get(`/public/form-token/${type}`).then(r => r.data),
  events: () => client.get("/public/events").then(r => r.data),
  eventBySlug: (slug) => client.get(`/public/events/${slug}`).then(r => r.data),
  registerEvent: (id, data) => client.post(`/public/events/${id}/register`, data).then(r => r.data),
  // AUTH
  login: (email, password) => client.post("/auth/login", { email, password }).then(r => r.data),
  me: () => client.get("/auth/me").then(r => r.data),
  // ADMIN
  adminPages: () => client.get("/admin/pages").then(r => r.data),
  adminPage: (id) => client.get(`/admin/pages/${id}`).then(r => r.data),
  updatePage: (id, body) => client.put(`/admin/pages/${id}`, body).then(r => r.data),
  addSection: (pageId, body) => client.post(`/admin/pages/${pageId}/sections`, body).then(r => r.data),
  updateSection: (pageId, sid, body) => client.put(`/admin/pages/${pageId}/sections/${sid}`, body).then(r => r.data),
  deleteSection: (pageId, sid) => client.delete(`/admin/pages/${pageId}/sections/${sid}`).then(r => r.data),
  duplicateSection: (pageId, sid) => client.post(`/admin/pages/${pageId}/sections/${sid}/duplicate`).then(r => r.data),
  reorderSections: (pageId, order) => client.post(`/admin/pages/${pageId}/sections/reorder`, { order }).then(r => r.data),
  adminFaq: () => client.get("/admin/faq").then(r => r.data),
  createFaq: (b) => client.post("/admin/faq", b).then(r => r.data),
  updateFaq: (id, b) => client.put(`/admin/faq/${id}`, b).then(r => r.data),
  deleteFaq: (id) => client.delete(`/admin/faq/${id}`).then(r => r.data),
  adminGloss: () => client.get("/admin/glossary").then(r => r.data),
  createGloss: (b) => client.post("/admin/glossary", b).then(r => r.data),
  updateGloss: (id, b) => client.put(`/admin/glossary/${id}`, b).then(r => r.data),
  deleteGloss: (id) => client.delete(`/admin/glossary/${id}`).then(r => r.data),
  adminNews: () => client.get("/admin/news").then(r => r.data),
  createNews: (b) => client.post("/admin/news", b).then(r => r.data),
  updateNews: (id, b) => client.put(`/admin/news/${id}`, b).then(r => r.data),
  duplicateNews: (id) => client.post(`/admin/news/${id}/duplicate`).then(r => r.data),
  reorderNews: (order) => client.post("/admin/news/reorder", { order }).then(r => r.data),
  deleteNews: (id) => client.delete(`/admin/news/${id}`).then(r => r.data),
  translateNews: (id) => client.post(`/admin/news/${id}/translate`).then(r => r.data),
  generateNewsSeo: (id, language = "de") => client.post(`/admin/news/${id}/seo-generate`, { language }).then(r => r.data),
  createEventFromNews: (id) => client.post(`/admin/news/${id}/create-event`).then(r => r.data),
  submissions: () => client.get("/admin/submissions").then(r => r.data),
  updateSubmission: (id, b) => client.put(`/admin/submissions/${id}`, b).then(r => r.data),
  adminEvents: () => client.get("/admin/events").then(r => r.data),
  createEvent: (b) => client.post("/admin/events", b).then(r => r.data),
  updateEvent: (id, b) => client.put(`/admin/events/${id}`, b).then(r => r.data),
  duplicateEvent: (id) => client.post(`/admin/events/${id}/duplicate`).then(r => r.data),
  deleteEvent: (id) => client.delete(`/admin/events/${id}`).then(r => r.data),
  eventRegistrations: (id) => client.get(`/admin/events/${id}/registrations`).then(r => r.data),
  updateEventRegistration: (id, b) => client.put(`/admin/event-registrations/${id}`, b).then(r => r.data),
  adminSettings: () => client.get("/admin/settings").then(r => r.data),
  updateSettings: (b) => client.put("/admin/settings", b).then(r => r.data),
  translate: (body) => client.post("/admin/translate", body).then(r => r.data),
  translateEverything: () => client.post("/admin/translate-everything").then(r => r.data),
  translateStatus: () => client.get("/admin/translate-everything/status").then(r => r.data),
  generateSeo: (page_id, language) => client.post("/admin/seo/generate", { page_id, language }).then(r => r.data),
  listTemplates: () => client.get("/admin/templates").then(r => r.data),
  applyTemplate: (key, page_id, mode = "append") => client.post(`/admin/templates/${key}/apply`, { page_id, mode }).then(r => r.data),
  // MEDIA
  adminMedia: () => client.get("/media").then(r => r.data),
  uploadMedia: (file, media_type = "image") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("media_type", media_type);
    return client.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  updateMedia: (id, b) => client.put(`/media/${id}`, b).then(r => r.data),
  deleteMedia: (id) => client.delete(`/media/${id}`).then(r => r.data),
  genAlt: (id) => client.post(`/media/${id}/generate-alt`).then(r => r.data),
  // Users
  createUser: (b) => client.post("/admin/users", b).then(r => r.data),
  listUsers: () => client.get("/admin/users").then(r => r.data),
  // PRESS
  adminPress: () => client.get("/admin/press").then(r => r.data),
  createPress: (b) => client.post("/admin/press", b).then(r => r.data),
  updatePress: (id, b) => client.put(`/admin/press/${id}`, b).then(r => r.data),
  deletePress: (id) => client.delete(`/admin/press/${id}`).then(r => r.data),
  // PRICING
  adminPricingSettings: () => client.get("/admin/pricing-settings").then(r => r.data),
  updatePricingSettings: (b) => client.put("/admin/pricing-settings", b).then(r => r.data),
};
